import React, { useState } from 'react';
import { LocationLevelItem } from '../../../interfaces/dashboards/PerformanceDashboard.interface';
import PerformanceDashboardService from '../../../services/dashboards/PerformanceDashboard.service';

interface LocationSalesBreakdownProps {
    locationLevelData: LocationLevelItem[];
    locations: string[];
    epochMonth: number;
    compareEpochMonth: number;
    compareDateString: string;
}

interface parsedLocationLevelData {
    locationCode: string;
    sales: number;
    compareSales: number;
}

const SalesComparisonCard = ({ locationCode, sales, compareSales, compareDateString }: { locationCode: string; sales: number; compareSales: number; compareDateString?: string }) => {
    let salesChangePercentage = compareSales ? sales / compareSales * 100 - 100 : 0;

    function getColorClass(salesChangePercentage: number) {
        if (salesChangePercentage > 0) {
            return 'text-emerald-500';
        } else if (salesChangePercentage < 0) {
            return 'text-red-500';
        } else {
            return 'text-gray-500';
        }
    }

    return (
        <div className="px-2 py-auto border rounded shadow text-center !w-[200px] !h-[200px] flex-shrink-0 flex flex-col justify-center">
            <h2 className="text-[18px] m-0">Store {locationCode}</h2>
            <p className="text-[26px] font-bold m-0">{PerformanceDashboardService.moneyFormat(sales, true)}</p>
            <p className="text-[14px] m-0">
                <span className={`text-[14px] ${getColorClass(salesChangePercentage)}`}>
                    {salesChangePercentage.toFixed(0)}% from {compareDateString}
                </span>
            </p>
        </div>
    );
};

export default function LocationSalesBreakdown({ locationLevelData, locations, epochMonth, compareEpochMonth, compareDateString }: LocationSalesBreakdownProps) {
    const [selectedLocation, setSelectedLocation] = useState("all-locations");

    const parsedLocationLevelData: parsedLocationLevelData[] = locationLevelData.filter(item => item.epochMonthRm === epochMonth)
        .map((item) => {
            const compareItem = locationLevelData.find(compareItem => compareItem.locationCode === item.locationCode && compareItem.epochMonthRm === compareEpochMonth);
            if (!compareItem) {
                return null; // Return null instead of undefined for clarity
            }
            const salesRm = item.salesRm;

            return {
                locationCode: item.locationCode,
                sales: salesRm,
                compareSales: item.trend ? compareItem.salesRm * item.trend : compareItem.salesRm,
            };
        }).filter(item => item !== null) as parsedLocationLevelData[];

    return (
        <div className="flex flex-col shadow-2xl p-5 rounded-xl border border-gray-300 bg-white">
            <div className="flex flex-row justify-between items-center mb-4">
                <h1 className="text-2xl font-bold tracking-tight">Location Sales Performance</h1>
                <div className="flex flex-row items-center">
                    <select
                        id="selectLocation"
                        value={selectedLocation}
                        onChange={(e) => { setSelectedLocation(e.target.value) }}
                        className="border border-gray-300 rounded p-2"
                    >
                        <option value="all-locations">All Locations</option>
                        <option value="top-performers">Top Performers</option>
                        <option value="bottom-performers">Bottom Performers</option>
                    </select>
                </div>
            </div>
            <div className="flex flex-row gap-4 overflow-x-auto">
                {parsedLocationLevelData
                    .filter((item) => {
                        if (selectedLocation === "all-locations") {
                            return true;
                        }
                        if (selectedLocation === "top-performers") {
                            return item.sales - item.compareSales > 0;
                        }
                        if (selectedLocation === "bottom-performers") {
                            return item.sales - item.compareSales <= 0;
                        }
                        return false;
                    })
                    .map((item) => (
                        <SalesComparisonCard
                            key={item.locationCode}
                            locationCode={item.locationCode}
                            sales={item.sales}
                            compareSales={item.compareSales}
                            compareDateString={compareDateString}
                        />
                    ))}
            </div>
        </div>
    )
}